import { useMutation } from '@apollo/client';
import startCase from 'lodash/startCase';
import { useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import { useTaskProcessor } from '@eluve/appointment-tasks';
import {
  Box,
  Combobox,
  ComboboxDropdown,
  ComboboxOption,
  ComboboxSelectButton,
  HStack,
  Icon,
  Label,
  NewButton,
  Textarea,
  useToast,
} from '@eluve/components';
import { insertTranscriptMutation } from '@eluve/frontend-appointment-hooks';
import { TestTranscripts } from '@eluve/llm-outputs';
import { useNamedLogger } from '@eluve/logger';
import { useTenantIdFromParams } from '@eluve/session-helpers';
import { usePeriodicRemoteFileSyncSelector } from '@eluve/user-local-files';

interface AddToTranscriptProps {
  appointmentId: string;
}

export const AddToTranscript: React.FC<AddToTranscriptProps> = ({
  appointmentId,
}) => {
  const toast = useToast();
  const logger = useNamedLogger('AddToTranscript');
  const [insertTranscript, { loading }] = useMutation(insertTranscriptMutation);
  const [transcript, setTranscript] = useState('');
  const tenantId = useTenantIdFromParams();
  const { taskProcessor } = useTaskProcessor();

  const hasPendingAppointmentTask = taskProcessor
    ?.getTasks()
    .some((t) => t.appointmentId === appointmentId);

  const hasPendingAppointmentUploadTasks = usePeriodicRemoteFileSyncSelector(
    (s) =>
      s.context.fileQueue?.some((q) => q.appointmentId === appointmentId) ||
      s.context.currentFile?.appointmentId === appointmentId,
  );

  const hasPendingTasks =
    hasPendingAppointmentTask || hasPendingAppointmentUploadTasks;

  const disabled = useMemo(
    () => !transcript.trim() || loading,
    [transcript, loading],
  );

  const addTranscriptToAppointment = async () => {
    if (!transcript) {
      return;
    }

    const segmentId = uuid();

    const { errors } = await insertTranscript({
      context: eluveAdminHasuraContext,
      variables: {
        tenantId: tenantId ?? '',
        appointmentId: appointmentId,
        transcript: transcript,
        segmentId,
      },
    });

    if (errors) {
      logger.error('Failed to upload transcript');
      toast.error('Failed to upload transcript');
      return;
    }

    setTranscript('');
  };

  return (
    <Box vStack>
      <Label className="px-2">Transcript</Label>
      <Textarea
        className="h-[70px] w-full resize-none text-text-primary outline-none focus-visible:ring-0"
        value={transcript ?? ''}
        placeholder="Paste transcript here..."
        onChange={(e) => setTranscript(e.target.value)}
      />
      <HStack>
        <NewButton
          disabled={disabled || hasPendingTasks}
          onClick={addTranscriptToAppointment}
          text="Add to transcript"
        />
        <Combobox>
          <ComboboxSelectButton
            className="w-full rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-secondary"
            dropdownIcon={
              <Icon className="ml-2 opacity-50" name="ChevronDown" size="xs" />
            }
          >
            ...or use a conversation
          </ComboboxSelectButton>
          <ComboboxDropdown searchPlaceholder="Search examples">
            {Object.entries(TestTranscripts).map(([key, value]) => (
              <ComboboxOption key={key} onSelect={() => setTranscript(value)}>
                {startCase(key)}
              </ComboboxOption>
            ))}
          </ComboboxDropdown>
        </Combobox>
      </HStack>
    </Box>
  );
};
